import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Activity, Loader, RefreshCw, AlertCircle, Sparkles, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RANGE_TABS = [
  { id: 'today',    label: 'Today' },
  { id: 'week',     label: 'This Week' },
  { id: 'month',    label: 'This Month' },
  { id: 'all_time', label: 'All Time' },
];

const formatDayLabel = (iso) => {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const StatCard = ({ label, starts, completions, accent }) => {
  const rate = starts > 0 ? Math.round((completions / starts) * 100) : null;
  return (
    <div
      data-testid={`kiosk-stat-${label.toLowerCase().replace(/\s/g, '-')}`}
      className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col gap-2"
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-gray-500">
          {label}
        </span>
        {rate !== null && (
          <span
            className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
            title="Completion rate"
          >
            {rate}%
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-2">
        <span
          className="text-3xl font-bold text-gray-900"
          style={{ fontFamily: 'Manrope, sans-serif', color: accent }}
        >
          {completions}
        </span>
        <span className="text-sm text-gray-500">
          completed
        </span>
      </div>
      <div className="text-xs text-gray-500">
        {starts} start{starts === 1 ? '' : 's'}
      </div>
    </div>
  );
};

const DayBars = ({ days }) => {
  const max = Math.max(1, ...days.map((d) => Math.max(d.starts, d.completions)));
  return (
    <div className="flex items-end gap-1 h-32">
      {days.map((d) => {
        const sH = (d.starts / max) * 100;
        const cH = (d.completions / max) * 100;
        return (
          <div
            key={d.date}
            data-testid={`kiosk-day-${d.date}`}
            className="flex-1 flex flex-col items-center justify-end gap-0.5 group"
            title={`${formatDayLabel(d.date)} · ${d.completions} done / ${d.starts} started`}
          >
            <div className="w-full flex items-end gap-0.5 h-full">
              <div
                className="flex-1 bg-gray-200 rounded-t-sm group-hover:bg-gray-300 transition-colors"
                style={{ height: `${sH}%`, minHeight: d.starts > 0 ? 2 : 0 }}
              />
              <div
                className="flex-1 bg-[#1f3d2b] rounded-t-sm group-hover:bg-[#152e20] transition-colors"
                style={{ height: `${cH}%`, minHeight: d.completions > 0 ? 2 : 0 }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const TYPE_LABELS = {
  creatine: 'Creatine',
  nitric_oxide: 'Nitric Oxide Booster',
  testosterone_support: 'Testosterone Support',
  protein: 'Protein',
  magnesium: 'Magnesium',
  theanine: 'L-Theanine',
  adaptogen: 'Adaptogens',
  digestive_enzymes: 'Digestive Enzymes',
  probiotic: 'Probiotic',
  collagen: 'Collagen',
  multivitamin: 'Multivitamin',
  coq10: 'Coenzyme Q10',
  b_complex: 'B-Complex',
  calcium_d: 'Calcium & Vitamin D',
};
const labelFor = (t) => TYPE_LABELS[t] || t.replace(/_/g, ' ');

const KioskAnalytics = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${API}/admin/kiosk-stats`);
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load kiosk stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  const cards = useMemo(() => {
    if (!data) return null;
    return RANGE_TABS.map((r) => ({
      id: r.id,
      label: r.label,
      starts: data.starts?.[r.id] || 0,
      completions: data.completions?.[r.id] || 0,
    }));
  }, [data]);

  if (loading && !data) {
    return (
      <div
        data-testid="kiosk-analytics-loading"
        className="bg-white border border-gray-200 rounded-xl p-6 flex items-center justify-center text-gray-500 gap-2"
      >
        <Loader className="animate-spin" size={18} /> Loading kiosk analytics…
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-red-200 rounded-xl p-6 flex items-start gap-3">
        <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
        <div className="flex-1">
          <h4 className="font-semibold text-red-900 mb-1">Kiosk analytics unavailable</h4>
          <p className="text-sm text-red-700">{error}</p>
        </div>
        <button
          onClick={fetchStats}
          className="text-sm text-red-700 underline font-semibold"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div data-testid="kiosk-analytics" className="bg-white border border-gray-200 rounded-xl p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-[#1f3d2b]/10 flex items-center justify-center">
            <Activity className="text-[#1f3d2b]" size={20} />
          </div>
          <div>
            <h2
              className="text-2xl font-bold text-gray-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Kiosk Analytics
            </h2>
            <p className="text-sm text-gray-600 mt-0.5">
              Customer assessments started vs completed over time.
            </p>
          </div>
        </div>
        <button
          data-testid="kiosk-stats-refresh"
          onClick={fetchStats}
          disabled={loading}
          className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 font-semibold rounded-lg px-4 py-2 transition-colors flex items-center gap-2"
        >
          {loading
            ? <Loader className="animate-spin" size={16} />
            : <RefreshCw size={16} />}
          Refresh
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {cards.map((c) => (
          <StatCard
            key={c.id}
            label={c.label}
            starts={c.starts}
            completions={c.completions}
            accent="#1f3d2b"
          />
        ))}
      </div>

      {/* Last 14 days chart */}
      <div className="border border-gray-200 rounded-xl p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3
              className="text-base font-bold text-gray-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Last 14 Days
            </h3>
            <p className="text-xs text-gray-500">
              <span className="inline-block w-2 h-2 bg-gray-300 rounded-sm mr-1" />
              Started
              <span className="inline-block w-2 h-2 bg-[#1f3d2b] rounded-sm ml-3 mr-1" />
              Completed
            </p>
          </div>
        </div>
        <DayBars days={data.by_day || []} />
        <div className="flex justify-between text-[10px] text-gray-400 mt-1">
          <span>{formatDayLabel(data.by_day?.[0]?.date || new Date().toISOString().slice(0, 10))}</span>
          <span>{formatDayLabel(data.by_day?.[data.by_day.length - 1]?.date || new Date().toISOString().slice(0, 10))}</span>
        </div>
      </div>

      {/* Top recommended categories */}
      <div className="border border-gray-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp size={16} className="text-[#d97736]" strokeWidth={2.5} />
          <h3
            className="text-base font-bold text-gray-900"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Top Recommendations · last 30 days
          </h3>
        </div>
        {(data.top_categories || []).length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
            <Sparkles size={16} className="text-gray-400" />
            No completed assessments yet — recommendations will appear here as customers use the kiosk.
          </div>
        ) : (
          <ul className="space-y-2">
            {data.top_categories.map((row, idx) => {
              const max = data.top_categories[0].count;
              const w = Math.max(8, Math.round((row.count / max) * 100));
              return (
                <li
                  key={row.type}
                  data-testid={`top-cat-${row.type}`}
                  className="flex items-center gap-3"
                >
                  <span className="w-5 text-[10px] font-bold text-gray-400 text-right">
                    {idx + 1}
                  </span>
                  <span className="w-44 text-sm text-gray-900 truncate" title={labelFor(row.type)}>
                    {labelFor(row.type)}
                  </span>
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#1f3d2b]"
                      style={{ width: `${w}%` }}
                    />
                  </div>
                  <span className="w-8 text-right text-sm font-semibold text-gray-900">
                    {row.count}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default KioskAnalytics;
